<template>
  <!--
    ตรวจสุขภาพ/ความสมบูรณ์ของข้อมูลพนักงานราชการ (govem_health.php)
    กรอบอัตรากำลัง (government_employee) เป็นข้อมูลหลัก → ทะเบียนบุคคล (personnel_temporary) ต้องตรงตาม
    สถานศึกษา/สถาบัน: หน่วยงานตนเอง (เซิร์ฟเวอร์บังคับ) · admin: ส่ง collegeCode
  -->
  <v-card outlined class="mb-4 health-card">
    <v-card-title class="py-2 subtitle-1 font-weight-bold">
      <v-progress-circular :value="score" :color="scoreColor" size="46" width="5" class="mr-3">
        <span class="caption font-weight-bold">{{ loading ? "…" : Math.round(score) }}</span>
      </v-progress-circular>
      <div>
        สุขภาพข้อมูลพนักงานราชการ
        <div class="caption grey--text text--darken-1">
          กรอบอัตรากำลังเป็นข้อมูลหลัก · ทะเบียนบุคคลต้องตรงตามกรอบ
          <span v-if="checkedAt"> · ตรวจเมื่อ {{ checkedAt }}</span>
        </div>
      </div>
      <v-spacer />
      <div class="text-right caption mr-2">
        <template v-if="lastConfirm">
          <v-icon small color="success">mdi-check-decagram</v-icon>
          ยืนยันความเป็นปัจจุบันล่าสุด {{ lastConfirm.confirmed_at }}
          <a class="ml-1" @click="openHistory">ประวัติ</a>
        </template>
        <span v-else class="warning--text">ยังไม่เคยยืนยันความเป็นปัจจุบัน</span>
      </div>
    </v-card-title>
    <v-card-text class="pb-2">
      <div class="d-flex flex-wrap align-center" style="gap:6px">
        <v-chip small outlined>ตำแหน่งในกรอบ {{ s.positions || 0 }} (ว่าง {{ s.vacant || 0 }})</v-chip>
        <v-chip small color="success" dark>ปกติ {{ s.ok || 0 }}/{{ s.persons || 0 }} คน</v-chip>
        <v-chip v-for="c in issueChips" :key="c.key" small :color="c.color" dark @click="filter = filter === c.key ? null : c.key">
          {{ c.text }} {{ c.count }}
        </v-chip>
        <v-chip v-if="s.salary" small outlined color="blue-grey" title="ข้อมูลประกอบ — ไม่ปรับทับ">
          ค่าตอบแทนกรอบ≠ทะเบียน {{ s.salary }}
        </v-chip>
        <v-chip v-if="s.pt_orphans" small outlined color="orange darken-2">รายชื่อในทะเบียนที่ไม่อยู่ในกรอบ {{ s.pt_orphans }}</v-chip>
        <v-spacer />
        <v-btn small text color="primary" :loading="loading" @click="check"><v-icon small left>mdi-refresh</v-icon>ตรวจอีกครั้ง</v-btn>
        <v-btn small text @click="showDetail = !showDetail">
          {{ showDetail ? "ซ่อนรายละเอียด" : "ดูรายละเอียด" }}
          <v-icon small right>{{ showDetail ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
        </v-btn>
        <v-btn small color="primary" :disabled="!s.syncable" :loading="syncing" @click="sync(null)">
          <v-icon small left>mdi-database-sync</v-icon>Update ทะเบียนบุคคลตามกรอบ ({{ s.syncable || 0 }} คน)
        </v-btn>
        <v-btn small color="success" :disabled="loading || s.syncable > 0" @click="confirmDialog = true">
          <v-icon small left>mdi-check-decagram</v-icon>ยืนยันความเป็นปัจจุบัน
        </v-btn>
      </div>

      <v-expand-transition>
        <div v-show="showDetail" class="mt-3">
          <v-alert type="info" dense text class="caption mb-2">
            <strong>Update</strong> ปรับทะเบียนบุคคลตามกรอบ: สร้างแถวที่ขาด · สังกัด/เลขที่ตำแหน่ง · ชื่อ-สกุล/วันเกิด/วันบรรจุ/ตำแหน่ง/วุฒิ
            (เฉพาะค่าที่กรอบมีข้อมูล ไม่ลบค่าในทะเบียน) · ไม่ปรับค่าตอบแทน (ยึดประวัติเงินเดือน/ผลการเลื่อน) ·
            ทุกการเปลี่ยนแปลงบันทึกใน Log และขอกู้คืนได้ · <strong>ข้อมูลกรอบไม่ครบ</strong> ต้องแก้ที่เมนู "แก้ไขข้อมูล" ของกรอบ
          </v-alert>
          <v-data-table :headers="headers" :items="rows" dense :items-per-page="10"
            :footer-props="{ 'items-per-page-options': [10, 25, 50, -1] }" item-key="id_card" no-data-text="ไม่มีรายการผิดปกติ">
            <template v-slot:[`item.full_name`]="{ item }">
              <div class="font-weight-medium">{{ item.full_name }}</div>
              <div class="caption grey--text">{{ item.id_card }} · ตำแหน่ง {{ item.id_position }}</div>
            </template>
            <template v-slot:[`item.issues`]="{ item }">
              <v-chip v-for="i in item.issues" :key="i" x-small dark :color="issue(i).color" class="mr-1 mb-1">{{ issue(i).text }}</v-chip>
            </template>
            <template v-slot:[`item.detail`]="{ item }">
              <div v-if="item.issues.includes('pt_missing')" class="caption">ไม่มีแถวในทะเบียนบุคคล — Update จะสร้างจากข้อมูลกรอบ</div>
              <div v-for="d in item.diffs" :key="d.field" class="caption">
                {{ d.label }}: <span class="red--text text--darken-1">{{ d.pt || "(ว่าง)" }}</span>
                → <span class="success--text text--darken-2 font-weight-medium">{{ d.frame }}</span>
              </div>
              <div v-for="(f, k) in item.frame_issues" :key="'f' + k" class="caption orange--text text--darken-3">• {{ f }}</div>
              <div v-if="item.salary" class="caption blue-grey--text">
                ค่าตอบแทน กรอบ {{ money(item.salary.frame) }} · ทะเบียน {{ money(item.salary.pt) }} (ข้อมูลประกอบ)
              </div>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <v-btn v-if="syncableItem(item)" x-small outlined color="primary" :loading="syncing === item.id_card" @click="sync(item)">
                Update
              </v-btn>
            </template>
          </v-data-table>
        </div>
      </v-expand-transition>
    </v-card-text>

    <!-- ยืนยันความเป็นปัจจุบัน -->
    <v-dialog v-model="confirmDialog" max-width="560">
      <v-card>
        <v-card-title class="subtitle-1">ยืนยันความเป็นปัจจุบันของข้อมูลพนักงานราชการ</v-card-title>
        <v-card-text>
          <p class="body-2">
            หน่วยงานรับรองว่าข้อมูลกรอบอัตรากำลังและทะเบียนบุคคลพนักงานราชการเป็นปัจจุบัน ณ วันนี้
            ({{ s.persons || 0 }} คน {{ s.positions || 0 }} ตำแหน่ง) — ระบบเก็บสำเนาผลตรวจไว้เป็นหลักฐาน (แก้ไข/ลบไม่ได้)
          </p>
          <v-alert v-if="frameIssueCount" type="warning" dense text class="caption">
            ยังมีข้อมูลในกรอบที่ต้องตรวจ {{ frameIssueCount }} คน — ระบุหมายเหตุ (เช่น อยู่ระหว่างต่อสัญญา)
          </v-alert>
          <v-textarea v-model="note" outlined dense rows="2" counter="500" label="หมายเหตุ" />
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="confirmDialog = false">ยกเลิก</v-btn>
          <v-spacer />
          <v-btn color="success" :loading="confirming" @click="confirm">ยืนยัน</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="historyDialog" max-width="760">
      <v-card>
        <v-card-title class="subtitle-1">ประวัติการยืนยันความเป็นปัจจุบัน</v-card-title>
        <v-card-text>
          <v-simple-table dense>
            <thead><tr><th>เมื่อ</th><th>คน/ตำแหน่ง</th><th>ค้างตรวจ</th><th>หมายเหตุ</th><th>ผู้ยืนยัน</th></tr></thead>
            <tbody>
              <tr v-for="h in history" :key="h.id">
                <td>{{ h.confirmed_at }}</td><td>{{ h.persons }}/{{ h.positions }}</td><td>{{ h.issues }}</td>
                <td class="caption">{{ h.note || "-" }}</td><td class="caption">{{ h.confirmed_role === "A" ? "ส่วนกลาง" : "หน่วยงาน" }} #{{ h.confirmed_by }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-snackbar v-model="snack.show" :color="snack.color" top :timeout="6000">{{ snack.text }}</v-snackbar>
  </v-card>
</template>

<script>
const API = "govem_health.php";
const ISSUES = {
  pt_missing: { text: "ไม่มีในทะเบียนบุคคล", color: "red darken-1" },
  affiliation: { text: "สังกัด/เลขที่ตำแหน่งไม่ตรง", color: "deep-orange" },
  fields: { text: "ข้อมูลไม่ตรงกรอบ", color: "orange darken-2" },
  frame: { text: "ข้อมูลกรอบไม่ครบ", color: "purple" }
};

export default {
  name: "GovemDataHealth",
  props: { collegeCode: { type: String, default: "" } },
  data() {
    return {
      ApiKey: "HRvec2021",
      loading: false,
      syncing: null,
      confirming: false,
      persons: [],
      s: {},
      checkedAt: "",
      lastConfirm: null,
      showDetail: false,
      filter: null,
      confirmDialog: false,
      historyDialog: false,
      history: [],
      note: "",
      snack: { show: false, text: "", color: "success" },
      headers: [
        { text: "พนักงานราชการ", value: "full_name", width: 230 },
        { text: "ความผิดปกติ", value: "issues", sortable: false, width: 200 },
        { text: "รายละเอียด (ทะเบียน → กรอบ)", value: "detail", sortable: false },
        { text: "", value: "actions", sortable: false, width: 80 }
      ]
    };
  },
  computed: {
    score() {
      return Number(this.s.score || 0);
    },
    scoreColor() {
      return this.score >= 95 ? "success" : this.score >= 70 ? "warning" : "error";
    },
    issueChips() {
      return Object.keys(ISSUES)
        .map(key => ({ key, ...ISSUES[key], count: this.s[key] || 0 }))
        .filter(c => c.count);
    },
    rows() {
      const bad = this.persons.filter(p => p.issues.length || p.salary);
      return this.filter ? bad.filter(p => p.issues.includes(this.filter)) : bad.filter(p => p.issues.length);
    },
    frameIssueCount() {
      return this.persons.filter(p => p.issues.includes("frame")).length;
    }
  },
  watch: {
    collegeCode() {
      this.check();
    }
  },
  mounted() {
    this.check();
  },
  methods: {
    issue(i) {
      return ISSUES[i] || { text: i, color: "grey" };
    },
    money(v) {
      return Number(v || 0).toLocaleString("th-TH");
    },
    syncableItem(p) {
      return p.issues.some(i => ["pt_missing", "affiliation", "fields"].includes(i));
    },
    async api(payload) {
      try {
        const res = await this.$http.post(API, { ApiKey: this.ApiKey, college_code: this.collegeCode || undefined, ...payload });
        if (!res.data || res.data.status !== true) throw new Error((res.data && res.data.message) || "ไม่ได้รับข้อมูล");
        return res.data;
      } catch (e) {
        const msg = e.response && e.response.data && e.response.data.message;
        throw new Error(msg || e.message);
      }
    },
    apply(d) {
      this.persons = d.persons || [];
      this.s = d.summary || {};
      if (d.checked_at) this.checkedAt = d.checked_at;
      if (d.last_confirm !== undefined) this.lastConfirm = d.last_confirm;
      this.$emit("summary", this.s);
    },
    async check() {
      this.loading = true;
      try {
        this.apply(await this.api({ action: "check" }));
      } catch (e) {
        this.snack = { show: true, text: e.message, color: "error" };
      } finally {
        this.loading = false;
      }
    },
    async sync(item) {
      const who = item ? item.full_name : `${this.s.syncable} คน`;
      if (!confirm(`Update ทะเบียนบุคคลของ ${who} ให้ตรงกับกรอบอัตรากำลัง?\n(บันทึกค่าเดิมไว้ใน Log — ขอกู้คืนได้)`)) return;
      this.syncing = item ? item.id_card : true;
      try {
        const d = await this.api({ action: "sync", id_cards: item ? [item.id_card] : undefined });
        this.apply(d);
        this.snack = { show: true, color: "success",
          text: `Update แล้ว — สร้าง ${d.done.created} คน · ปรับ ${d.done.updated} คน (${d.done.fields} รายการข้อมูล)` };
        this.$emit("synced");
      } catch (e) {
        this.snack = { show: true, text: e.message, color: "error" };
      } finally {
        this.syncing = null;
      }
    },
    async confirm() {
      this.confirming = true;
      try {
        const d = await this.api({ action: "confirm", note: this.note });
        this.lastConfirm = d.last_confirm;
        this.confirmDialog = false;
        this.note = "";
        this.snack = { show: true, text: "บันทึกการยืนยันความเป็นปัจจุบันแล้ว", color: "success" };
      } catch (e) {
        this.snack = { show: true, text: e.message, color: "error" };
      } finally {
        this.confirming = false;
      }
    },
    async openHistory() {
      this.historyDialog = true;
      try {
        this.history = (await this.api({ action: "history" })).history;
      } catch (e) {
        this.snack = { show: true, text: e.message, color: "error" };
      }
    }
  }
};
</script>

<style scoped>
.health-card {
  border-left: 4px solid #1565c0 !important;
}
</style>
